'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";
import { parse } from 'qs';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {isFalse} from '@uw-it-sis/lib-react/lib/Utils';
import Loading from '@uw-it-sis/lib-react/lib/Loading';
import HelpPopover from '@uw-it-sis/lib-react/lib/HelpPopover';
import Broken from "@uw-it-sis/lib-react/lib/Broken";
import UnknownState from "@uw-it-sis/lib-react/lib/UnknownState";
import DocumentTitle from '@uw-it-sis/lib-react/lib/DocumentTitle';
import {withRouter} from "@uw-it-sis/lib-react/lib/WithRouter";

import CampusSelector from '@uw-it-sis/lib-react-myplan/lib/CampusSelector';
import RelatedLinks from "@uw-it-sis/lib-react-myplan/lib/RelatedLinks";

import courseSearchStore from './CourseSearchStore';
import CourseSearchStart from './CourseSearchStart';
import QueryStringTextField from "./QueryStringTextField";
import CourseSearchNoResults from './CourseSearchNoResults';
import CourseSearchResultsPanel from './CourseSearchResultsPanel';
import TimeRangeSelector from "./TimeRangeSelector";
import Resources from '../common/Resources';

/**
 * Course Search page
 */
class CourseSearch extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.store = courseSearchStore;

        this.onChange = this.onChange.bind(this);
        this.onClearSearch = this.onClearSearch.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onShowBrowse = this.onShowBrowse.bind(this);
        this.onToggleAdvancedSearch = this.onToggleAdvancedSearch.bind(this);
        this.analyticsStore = appStore.getAnalyticsStore();
    }
    
    componentDidMount() {
       this._initializeStore();
    }

    /**
     * When we navigate via the links for GenEd or subject areas on the course browse page,
     * the component is already mounted and will receive new props via componentWillReceiveProps
     *
     * NOTE: This method gets called every single time parent updates, so you have to check new props with the old props
     * and update on every call ... else it will be really slow and probably throw a react error.
     *
     * @param prevProps The props before the current update.
     */
    componentDidUpdate(prevProps) {
        // See if anything updated in the search param. If so, then re-init. If not then just return.
        if (this.props.location.search === prevProps.location.search) {
            return;
        }
        this._initializeStore();
    }

    /**
     * Parses the request and initializes the store.
     * @private
     */
    _initializeStore() {
        // Look for a 'campus' param. This param comes from Links in the catalog too. It should remain undefined if
        // there isn't one, so that the store init works correctly.
        const query = parse(this.props.location.search, { ignoreQueryPrefix: true });

        let campus = undefined;
        if (query.campus) {
            campus = query.campus;
        }

        // Looks for an isSectionSearch param on the URL. The GenEd links use these. Again, leave undefined if no param.
        let isSectionSearch = undefined;
        switch (query.isSectionSearch) {
            case 'false':
                isSectionSearch = false;
                break;
            case 'true':
                isSectionSearch = true;
                break;
        }

        // Look for a 'qs' property. This is what will go into the search text box.
        let queryString = query.qs;
        this.store.mount(campus, isSectionSearch, queryString);
    }

    /**
     * Handle input widget changes.
     */
    onChange = (e) => {
        let property = e.target.name;
        let value = "";
        if (e.target.type === "radio") {
            value = e.target.value;
        } else {
            value = e.target.checked;
        }
        //  Handle checkboxes.
        if (property.indexOf("include") === 0) {
            //  Have to invert this value
            value = !e.target.checked;
        }
        if (property === "isSectionSearch") {
            // log when user switches between 'Time Schedule' and 'Course Catalog'
            let eventStr = (value === 'true' ? "Search-SectionSearch-Selection" : "Search-CatalogSearch-Selection");
            this.analyticsStore.addEvent(eventStr, {}, {});
            // when changing search types, clear the results as result data isn't consistent across search types
            this.onClearSearch();
        }
        this.store.updateCourseSearchFormData(property, value);
    };

    onClearSearch = () => {
        this.analyticsStore.addEvent("Search-ClearSearch", {}, {});
        this.store.clearSearch();
    };

    updateNewSearch = (newSearch) => this.store.updateNewSearch(newSearch);

    onSubmit = (e) => {
        e.preventDefault();  //  Prevent POST and reload.
        e.stopPropagation();

        // A new search has been fired
        this.updateNewSearch(true);
        this.store.search();
    };

    onToggleAdvancedSearch = () => this.store.toggleAdvancedSearchPanel();

    /**
     * Handler for show browse click.
     */
    onShowBrowse = () => this.store.showBrowseMethod();

    render() {
        switch (this.store.state) {
            case States.done:
                // Proceed.
                break;
            case States.error:
                return <Broken name="CourseSearch.ResultsPanel"/>;
                break;
            case States.pending:
            case States.initial:
                 return <Loading type="spinner2" alt="Loading ..."/>;
            default:
                return <UnknownState name="CourseSearch.ResultsPanel"/>;
        }

        let resultsPanel = null;
        const campus = this.store.getCampus();
        const formData = this.store.data.formData;

        /*
         * If the user has searched and the results count is zero then show the no results component.
         * Otherwise, if a search is in progress show the loading indicator. Finally, if there are results
         * then show the results panel. Note: The results widget has its own blocking mechanisms which are applied
         * during sorting and filtering.
         */
        if (this.store.data.hasSearched) {
            if (this.store.searchState === States.pending) {
                resultsPanel =  <Loading type="spinner2" alt="Searching ..."/>;
            } else {
                if (this.store.getSearchResultsCount() > 0) {
                    resultsPanel =
                        <CourseSearchResultsPanel
                            courses={this.store.allCourses}
                            queryString={this.store.lastQueryString}
                            newSearch={this.store.newSearch}
                            sectionFiltersMethod={this.store.getSectionFiltersObject}
                            resultsPerPage={100} />;
                } else {
                    resultsPanel = <CourseSearchNoResults />;
                }
            }
        } else {
            resultsPanel =
                <CourseSearchStart campus={campus} isSectionSearch={formData.isSectionSearch}
                                   showBrowse={this.store.data.showBrowse} showBrowseHandler={this.onShowBrowse}/>;
        }

        let clearButton =
            <Col xs={12} md={6} className="mt-3 mt-md-0 d-grid">
                <Button variant="link" onClick={this.onClearSearch}>Clear Search</Button>
            </Col>;

        /*
         * Determine if the search button should be disabled.
         * Disable when the state is pending, or when there is no campus and the search is disabled.
         */
        let disableSearchButton = false;
        if (this.store.searchState === States.pending || this.store.searchDisabled || isFalse(formData.campus)) {
            disableSearchButton = true;
        }

        /*
         * Determine the button label. If search is empty, show 'search', if there are results, show 'update', if the
         * action is pending, show 'searching...'
         */
        let searchButtonLabel = ( courseSearchStore.allCourses.length > 0 ? 'Update':'Search' );
        if ( this.store.searchState === States.pending ) {
            searchButtonLabel = <span className="ellipsis-loading">Searching</span>;
        }

        const isSectionSearch = courseSearchStore.isSectionSearch();


        // Data structure for days widget.
        let days = [
            {id: '1', name: 'Mon', long: 'Monday'}, {id: '2', name: 'Tue', long: 'Tuesday'},
            {id: '3', name: 'Wed', long: 'Wednesday'}, {id: '4', name: 'Thu', long: 'Thursday'},
            {id: '5', name: 'Fri', long: 'Friday'}, {id: '6', name: 'Sat', long: 'Saturday'}
        ];
        // Build days buttons.
        let daysFilters = days.map(function (day, i) {
            return (
                <Form.Check id={"search-form-meeting-day-" + day.id}
                            type="checkbox"
                            inline
                            key={i}>
                    <Form.Check.Input type="checkbox"
                                      name={"days:" + day.id}
                                      checked={formData.days[day.id]}
                                      disabled={! isSectionSearch || ! formData.campus}
                                      onChange={this.onChange} />
                    <Form.Check.Label type="checkbox">
                        <span aria-hidden={true}>{day.name}</span>
                        <span className="sr-only">{day.long}</span>
                    </Form.Check.Label>
                </Form.Check>
            );
        }, this);

        const pceSectionsOnlyLabel =
            <span>Find <abbr title="Professional & Continuing Education">PCE</abbr> sections only</span>;

        const form =
            <form>
                <div id="course-search-form" className="search-form border-bottom border-dotted border-top-0 border-start-0 border-end-0 pb-3" role="search">
                    <Row>
                        <Col xs={12}>
                            <fieldset role="group">
                                <Form.Group className="d-sm-flex align-items-center mb-3">
                                    <legend className="fw-bolder fs-6 mb-2 mb-sm-0 me-sm-4">Search in:</legend>
                                    <Form.Check id="search-form-time-schedule"
                                                type="radio"
                                                inline>
                                        <Form.Check.Input type="radio"
                                                          name="isSectionSearch"
                                                          value="true"
                                                          checked={isSectionSearch}
                                                          disabled={! formData.campus}
                                                          onChange={this.onChange}/>
                                        <Form.Check.Label type="radio">Time Schedule</Form.Check.Label>
                                    </Form.Check>
                                    <Form.Check id="search-form-course-catalog"
                                                type="radio"
                                                inline>
                                        <Form.Check.Input type="radio"
                                                          name="isSectionSearch"
                                                          value="false"
                                                          checked={! isSectionSearch}
                                                          disabled={! formData.campus}
                                                          onChange={this.onChange}/>
                                        <Form.Check.Label type="radio">Course Catalog</Form.Check.Label>
                                    </Form.Check>
                                </Form.Group>
                            </fieldset>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={6} lg={7}>
                            <Form.Group className="search-form-query">
                                <Form.Label className="fw-bolder" htmlFor="search-query">Course search by:</Form.Label>
                                <QueryStringTextField name="searchQuery"
                                                      limit={10}
                                                      queryString={formData.queryString}
                                                      maxLength={120}
                                                      disabled={! formData.campus}
                                                      campus={formData.campus}
                                                      isSectionSearch={isSectionSearch} />
                                <Form.Text>
                                    Try: course code; course level (1xx); curriculum code; number of credits;
                                    instructor's last name; Gen Ed code; or keyword.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} lg={5}>
                            <span aria-hidden={true} className="d-none d-md-inline-block mb-2">&nbsp;</span>
                            <Row>
                                <Col xs={12} md={6} className="d-grid">
                                    <Button type="submit"
                                            onClick={this.onSubmit}
                                            disabled={disableSearchButton}
                                            variant="primary">
                                        {searchButtonLabel}
                                    </Button>
                                </Col>
                                {this.store.data.hasSearched ? clearButton : null}
                            </Row>
                        </Col>
                    </Row>

                     {/* Section search components */}

                    <Row className={isSectionSearch ? 'd-flex':'d-none'}>
                        <Col xs={12} sm={12}>
                            <fieldset role="group" className="my-3">
                                <Form.Group as={Row} >
                                    <Form.Label as="legend" column xs={12}>Meeting days and times:</Form.Label>
                                    <Col xs={12} sm={12} md={7} lg={5}>
                                        {daysFilters}
                                    </Col>
                                    <Col xs={12} sm={12} md={5} lg={3}>
                                        <TimeRangeSelector
                                            disabled={! isSectionSearch || ! formData.campus}
                                            handleEnterKeyPress={! disableSearchButton ? this.onSubmit : undefined}
                                            startEndTimes={formData.startEndTimes}
                                        />
                                    </Col>
                                </Form.Group>
                            </fieldset>
                        </Col>
                        { /* <Col xs={12} md={5} lg={4}>
                            <fieldset role="group">
                                <Form.Group as={Row}>
                                    <Form.Label as="legend" column xs={12}>
                                        <span className="text-nowrap d-flex align-items-center">
                                            <span className="me-2">Instruction format:</span>
                                            <HelpPopover title={Resources.HELP_CONTENT.instruction.title} placement="top">{Resources.HELP_CONTENT.instruction.contents}</HelpPopover>
                                        </span>
                                    </Form.Label>
                                    <Col xs={12}>
                                        <Form.Check id="option-inperson-only"
                                                    type="checkbox"
                                                    className="d-flex align-items-start"
                                                    inline>
                                            <Form.Check.Input type="checkbox"
                                                              name="showOnlyInPersonSections"
                                                              checked={formData.showOnlyInPersonSections}
                                                              disabled={! isSectionSearch || ! formData.campus}
                                                              onChange={this.onChange}
                                                              className="mt-1" />
                                            <Form.Check.Label type="checkbox">Find courses that include in-person and/or TBA sections</Form.Check.Label>
                                        </Form.Check>
                                    </Col>
                                </Form.Group>
                            </fieldset>
                        </Col>
                        <Col xs={12} md={7} lg={8}>*/}
                        <Col xs={12} md={8}>
                            <fieldset role="group">
                                <Form.Group as={Row}>
                                    <Form.Label as="legend" column xs={12}>Exclude options:</Form.Label>
                                    <Col xs={12}>
                                        <Form.Check id="exclude-prerequisites"
                                                    type="checkbox"
                                                    inline
                                                    name="includePrereqsRequired"
                                                    checked={! formData.includePrereqsRequired}
                                                    onChange={this.onChange}
                                                    disabled={! isSectionSearch || ! formData.campus}
                                                    label="Has prerequisites" />
                                        <Form.Check id="exclude-closed-sections"
                                                    type="checkbox"
                                                    inline
                                                    name="includeNonOpenSections"
                                                    checked={! formData.includeNonOpenSections}
                                                    onChange={this.onChange}
                                                    disabled={! isSectionSearch || ! formData.campus}
                                                    label="Closed sections" />
                                        <Form.Check id="exclude-tba-sections"
                                                    type="checkbox"
                                                    inline
                                                    name="includeTbaSections"
                                                    checked={! formData.includeTbaSections}
                                                    onChange={this.onChange}
                                                    disabled={! isSectionSearch || ! formData.campus}
                                                    label="Unscheduled (TBA) sections" />
                                        <Form.Check id="exclude-restricted-sections"
                                                    type="checkbox"
                                                    inline
                                                    name="includeRestrictedSections"
                                                    checked={! formData.includeRestrictedSections}
                                                    onChange={this.onChange}
                                                    disabled={! isSectionSearch || ! formData.campus}
                                                    label="Faculty/Add codes required" />
                                    </Col>
                                </Form.Group>
                            </fieldset>
                        </Col>
                        { /*<Col xs={12} md={5} lg={4}>*/ }
                        <Col xs={12} md={4}>
                            <fieldset role="group">
                                <Form.Group as={Row}>
                                    <Form.Label as="legend" column xs={12}>
                                        <span className="text-nowrap d-flex align-items-center">
                                            <span className="me-2">PCE Program Students:</span>
                                            <HelpPopover title={Resources.HELP_CONTENT.pce.title} placement="top">{Resources.HELP_CONTENT.pce.contents}</HelpPopover>
                                        </span>
                                    </Form.Label>
                                    <Col xs={12}>
                                        <Form.Check id="option-pce-only"
                                                    type="checkbox"
                                                    inline
                                                    name="showOnlyPceSections"
                                                    checked={ formData.showOnlyPceSections }
                                                    onChange={this.onChange}
                                                    disabled={! isSectionSearch || ! formData.campus}
                                                    label={pceSectionsOnlyLabel} />
                                    </Col>
                                </Form.Group>
                            </fieldset>
                        </Col>
                    </Row>
                </div>
                {resultsPanel}
            </form>;

        // Only authenticated users get a link to bookmarks.
        let searchLinks = null;
        if (appStore.isAuthenticated()) {
            const relatedLinksList = [
                {
                    label: "View Bookmarks",
                    href: "/bookmarks"
                }
            ];

            searchLinks = <RelatedLinks links={relatedLinksList} />;
        }

        return (
            <DocumentTitle title="Find Courses - MyPlan">
                <>
                    <div className="page-heading">
                        <h1>Find Courses</h1>
                    </div>
                    <CampusSelector campus={formData.campus} />
                    {form}
                    {searchLinks}
                </>
            </DocumentTitle>
        );
    }
}

export default withRouter(observer(CourseSearch));